import { DISTANCE_SORT_BY } from '@/features/listing/constants';
import { useListingContext } from '@/features/listing/hooks';
import { ProviderLocation } from '@/features/profile/profile.types';
import { useOasApp } from '@/providers';

import { useDistanceContext } from './use-distance-context';

export type GetProviderActiveLocation = (
  defaultProviderLocations: ProviderLocation[],
  physicianNpi: string
) => ProviderLocation;

const findProviderLocation =
  (sortBy: string) =>
  (activeLocationId: string, providerLocations: ProviderLocation[]): ProviderLocation => {
    if (sortBy === DISTANCE_SORT_BY && !activeLocationId) {
      // Return the closest location
      const closestLocation = providerLocations.reduce((acc, currentLocation) => {
        const currentDistanceAsNumber = Number(currentLocation.distance);
        const accDistanceAsNumber = Number(acc.distance);

        if (typeof acc.distance === 'undefined' && currentDistanceAsNumber >= 0) {
          return currentLocation;
        }
        if (typeof acc.distance !== 'undefined' && currentDistanceAsNumber >= 0) {
          return currentDistanceAsNumber < accDistanceAsNumber ? currentLocation : acc;
        }
        return acc;
      }, {} as ProviderLocation);

      // If no location with distance found, fallback to mainProviderLocation
      if (!closestLocation.id) {
        return providerLocations.find((loc) => loc.mainProviderLocation) || providerLocations[0];
      }

      return closestLocation;
    }

    // The default location is the location that has mainProviderLocation set to true.
    // The active location is the one selected by the user.
    // If no active location is selected, we fall back to the mainProviderLocation.

    const activeSelectedOrDefault =
      providerLocations.find((loc) => loc.id === activeLocationId) ||
      providerLocations.find((loc) => loc.mainProviderLocation) ||
      // Note: This should never happen, but in case no mainProviderLocation is set,
      // we fall back to the first location in the list so app doesn't break.
      providerLocations[0];

    return activeSelectedOrDefault;
  };

export const useProviderLocation = (): GetProviderActiveLocation => {
  const { appliedFilters } = useListingContext();
  const sortBy = appliedFilters?.sortType || '';
  const { providerLocations, activeProviderLocationByDoctorId } = useDistanceContext();
  const { isLocation, dotcms } = useOasApp();

  const getProviderActiveLocation = (
    defaultProviderLocations: ProviderLocation[],
    physicianNpi: string
  ) => {
    // only if market site is a location
    const activeLocationsFromSession =
      isLocation && !dotcms?.dotCmsHostId
        ? JSON.parse(sessionStorage.getItem('activeProviderLocations') || '{}')
        : {};

    const activeLocationId =
      activeLocationsFromSession[physicianNpi] || activeProviderLocationByDoctorId[physicianNpi];

    if (!providerLocations?.length && !defaultProviderLocations?.length) {
      return {} as ProviderLocation;
    }

    const foundLocation = findProviderLocation(sortBy)(
      activeLocationId,
      defaultProviderLocations || providerLocations
    );

    return foundLocation;
  };

  return getProviderActiveLocation;
};
